/**
 * FoilPicker — the "Replace foil" control for the high-lift workbench's Design rail (FXC-9009).
 *
 * Three ways to set the main element's base airfoil, all funnelled through the existing
 * {@link conditionFoil} (Selig/Lednicer normalise + TE-close) → `setters.setMainCoords`:
 *   1. SEARCH the bundled Flexfoil/UIUC catalog (~1665 foils, lazy-loaded on first focus) + the
 *      built-in trio, via a typed datalist.
 *   2. NACA 4-digit code → {@link naca4digit}.
 *   3. Upload a Selig/Lednicer `.dat` file → {@link parseAirfoilDat}.
 *
 * Replaces the original 3-entry <select>. Keeps the `data-tour="hlw-replace-foil"` anchor so the
 * tutorial still targets it.
 */
import { useMemo, useRef, useState, type CSSProperties } from "react";

import {
  FOIL_LIBRARY,
  mainCoordsDigest,
  naca4digit,
  conditionFoil,
  loadAirfoilCatalog,
  reshapePoints,
  parseAirfoilDat,
  type AirfoilCatalogEntry,
  type HighLiftAirfoil,
} from "../geometry";
import type { FoilSetters } from "./useFoilDesign";
import type { HighliftPalette } from "./useHighliftTheme";

type Status = { kind: "ok" | "err"; msg: string } | null;

const DATALIST_ID = "hlw-foil-catalog-list";

export function FoilPicker({
  config,
  setters,
  palette,
}: {
  config: HighLiftAirfoil;
  setters: FoilSetters;
  palette: HighliftPalette;
}) {
  const [catalog, setCatalog] = useState<AirfoilCatalogEntry[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [naca, setNaca] = useState("");
  const [status, setStatus] = useState<Status>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // The live foil's name, if it matches a built-in (catalog foils aren't digest-matched — they show
  // as the last thing typed / the placeholder).
  const currentDigest = mainCoordsDigest(config.main.coords);
  const builtinName = useMemo(
    () => FOIL_LIBRARY.find((f) => mainCoordsDigest(f.coords) === currentDigest)?.name ?? null,
    [currentDigest],
  );

  const readCatalog = async () => {
    if (catalog) return catalog;
    setLoading(true);
    try {
      const loaded = await loadAirfoilCatalog();
      setCatalog(loaded);
      return loaded;
    } finally {
      setLoading(false);
    }
  };

  const ensureCatalog = () => {
    if (catalog || loading) return;
    void readCatalog();
  };

  const applyName = async (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const builtin = FOIL_LIBRARY.find((f) => f.name === trimmed);
    if (builtin) {
      setters.setMainCoords(builtin.coords);
      setStatus({ kind: "ok", msg: `Loaded ${builtin.name}` });
      return;
    }
    const entry = (await readCatalog()).find((c) => c.name === trimmed);
    if (entry) {
      try {
        setters.setMainCoords(conditionFoil(reshapePoints(entry.points)));
        setStatus({ kind: "ok", msg: `Loaded ${entry.name}` });
      } catch {
        setStatus({ kind: "err", msg: `Couldn’t load ${entry.name} — invalid coordinates` });
      }
      return;
    }
    setStatus({ kind: "err", msg: `No foil named “${trimmed}”` });
  };

  const applyNaca = () => {
    const code = naca.replace(/\D/g, "");
    if (code.length !== 4) {
      setStatus({ kind: "err", msg: "Enter a 4-digit NACA code (e.g. 2412)" });
      return;
    }
    try {
      setters.setMainCoords(naca4digit(code));
      setStatus({ kind: "ok", msg: `Loaded NACA ${code}` });
    } catch {
      setStatus({ kind: "err", msg: `Invalid NACA ${code}` });
    }
  };

  const onFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const pts = parseAirfoilDat(String(reader.result ?? ""));
        if (pts.length < 10) throw new Error("too few points");
        setters.setMainCoords(conditionFoil(pts));
        setStatus({ kind: "ok", msg: `Loaded ${file.name} (${pts.length} pts)` });
      } catch {
        setStatus({ kind: "err", msg: `Couldn’t parse ${file.name} — not a valid airfoil .dat` });
      }
    };
    reader.onerror = () => setStatus({ kind: "err", msg: `Couldn’t read ${file.name}` });
    reader.readAsText(file);
  };

  const label: CSSProperties = {
    fontSize: 10.5,
    color: palette.ink4,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    fontWeight: 600,
    whiteSpace: "nowrap",
    width: 30,
  };
  const field: CSSProperties = {
    flex: 1,
    minWidth: 0,
    fontSize: 11,
    padding: "2px 5px",
    borderRadius: 4,
    background: palette.surface2,
    border: `1px solid ${palette.line}`,
    color: palette.ink,
  };
  const btn: CSSProperties = {
    fontSize: 11,
    padding: "2px 7px",
    borderRadius: 4,
    background: palette.surface2,
    border: `1px solid ${palette.line}`,
    color: palette.ink2,
    cursor: "pointer",
    whiteSpace: "nowrap",
  };

  const catalogCount = catalog?.length ?? 0;

  return (
    <div data-tour="hlw-replace-foil" style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 8 }}>
      {/* Searchable catalog (built-ins + lazy-loaded Flexfoil/UIUC) */}
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={label}>Foil</span>
        <input
          list={DATALIST_ID}
          aria-label="Search and select a base foil"
          placeholder={loading ? "loading catalog…" : (builtinName ?? "search foils…")}
          value={query}
          style={field}
          onFocus={ensureCatalog}
          onChange={(e) => {
            const v = e.target.value;
            setQuery(v);
            // datalist selection delivers the full name in one change — apply on exact match.
            if (FOIL_LIBRARY.some((f) => f.name === v) || catalog?.some((c) => c.name === v)) void applyName(v);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") void applyName(query);
          }}
        />
        <datalist id={DATALIST_ID}>
          {FOIL_LIBRARY.map((f) => (
            <option key={`b:${f.name}`} value={f.name} />
          ))}
          {catalog?.map((c) => (
            <option key={c.name} value={c.name} />
          ))}
        </datalist>
      </div>

      {/* NACA 4-digit parametric */}
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={label}>NACA</span>
        <input
          aria-label="NACA 4-digit code"
          placeholder="e.g. 2412"
          inputMode="numeric"
          maxLength={4}
          value={naca}
          style={{ ...field, flex: "0 0 80px" }}
          onChange={(e) => setNaca(e.target.value.replace(/\D/g, "").slice(0, 4))}
          onKeyDown={(e) => {
            if (e.key === "Enter") applyNaca();
          }}
        />
        <button type="button" style={btn} onClick={applyNaca} title="Generate a NACA 4-digit section">
          Set
        </button>
        <button
          type="button"
          style={{ ...btn, marginLeft: "auto" }}
          onClick={() => fileRef.current?.click()}
          title="Upload a Selig/Lednicer .dat airfoil file"
        >
          ↑ .dat
        </button>
        <input
          ref={fileRef}
          type="file"
          accept=".dat,.txt,.csv,text/plain"
          style={{ display: "none" }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onFile(f);
            e.target.value = ""; // allow re-uploading the same file
          }}
        />
      </div>

      {/* status / catalog hint */}
      {status ? (
        <span style={{ fontSize: 10, color: status.kind === "err" ? palette.warn : palette.ink3 }}>{status.msg}</span>
      ) : catalogCount > 0 ? (
        <span style={{ fontSize: 10, color: palette.ink4 }}>{catalogCount} foils · type to search, or load a NACA code / .dat</span>
      ) : null}
    </div>
  );
}
